import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, User, Briefcase, MapPin, Heart, Plane } from "lucide-react";
import HavnsLogo from "../components/HavnsLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Onboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    age: "",
    gender: "",
    location: "",
    profession: "",
    company: "",
    bio: "",
    expertise: "",
    hobbies: "",
    travel_bucket_list: "",
    looking_for: "",
    statement_of_intent: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.name || !formData.email || !formData.phone || !formData.age) {
          toast.error("Please fill all required fields");
          return false;
        }
        if (parseInt(formData.age) < 21) {
          toast.error("You must be at least 21 years old to join Havns");
          return false;
        }
        return true;
      case 2:
        if (!formData.profession || !formData.location) {
          toast.error("Please fill profession and location");
          return false;
        }
        return true;
      case 3:
        if (!formData.hobbies || !formData.looking_for) {
          toast.error("Please tell us about your interests");
          return false;
        }
        return true;
      case 4:
        if (!formData.statement_of_intent) {
          toast.error("Please share why you want to join Havns");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    
    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        age: parseInt(formData.age),
        gender: formData.gender,
        location: formData.location,
        profession: formData.profession,
        company: formData.company,
        bio: formData.bio,
        expertise: formData.expertise.split(",").map((s) => s.trim()).filter(Boolean),
        hobbies: formData.hobbies.split(",").map((s) => s.trim()).filter(Boolean),
        travel_bucket_list: formData.travel_bucket_list.split(",").map((s) => s.trim()).filter(Boolean),
        looking_for: formData.looking_for,
        statement_of_intent: formData.statement_of_intent,
      };

      const response = await axios.post(`${API}/profiles/create`, payload);
      
      if (response.data.success) {
        toast.success("Profile created successfully! We'll review your application.");
        // Store profile ID for later
        localStorage.setItem("havns_profile_id", response.data.profile_id);
        setCurrentStep(5); // Success step
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create profile");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, label: "Basics", icon: User },
    { num: 2, label: "Professional", icon: Briefcase },
    { num: 3, label: "Interests", icon: Heart },
    { num: 4, label: "Intent", icon: Plane },
  ];

  return (
    <div className="min-h-screen bg-[#FBF9F7]">
      {/* Header */}
      <header className="border-b border-[#EEE] bg-[#FBF9F7]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between h-20">
          <HavnsLogo className="text-2xl" linkTo="/" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-16">
        {currentStep < 5 && (
          <>
            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-2 mb-12">
              {steps.map((step, idx) => (
                <div key={step.num} className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      currentStep >= step.num
                        ? "bg-[#1A1A1A] text-white"
                        : "bg-[#EEE] text-[#999]"
                    }`}
                  >
                    {currentStep > step.num ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  {idx < steps.length - 1 && (
                    <div
                      className={`w-12 h-0.5 mx-2 ${
                        currentStep > step.num ? "bg-[#1A1A1A]" : "bg-[#EEE]"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Step 1: Basic Info */}
            {currentStep === 1 && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <h1 className="font-serif text-3xl text-[#1A1A1A] mb-2">Let's get to know you</h1>
                  <p className="text-[#666]">Start with the basics</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Full Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      className="h-12 rounded-xl border-[#DDD] focus:border-[#1A1A1A]"
                      data-testid="input-name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Email *</label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className="h-12 rounded-xl border-[#DDD] focus:border-[#1A1A1A]"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Phone Number *</label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 98765 43210"
                      className="h-12 rounded-xl border-[#DDD] focus:border-[#1A1A1A]"
                      data-testid="input-phone"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Age *</label>
                      <Input
                        name="age"
                        type="number"
                        value={formData.age}
                        onChange={handleInputChange}
                        placeholder="25"
                        min="21"
                        className="h-12 rounded-xl border-[#DDD] focus:border-[#1A1A1A]"
                        data-testid="input-age"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Gender</label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        className="w-full h-12 rounded-xl border border-[#DDD] focus:border-[#1A1A1A] px-4 bg-white"
                        data-testid="input-gender"
                      >
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="non-binary">Non-binary</option>
                        <option value="prefer-not-to-say">Prefer not to say</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Professional Info */}
            {currentStep === 2 && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <h1 className="font-serif text-3xl text-[#1A1A1A] mb-2">Your professional life</h1>
                  <p className="text-[#666]">Help us understand your background</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Profession/Role *</label>
                    <Input
                      name="profession"
                      value={formData.profession}
                      onChange={handleInputChange}
                      placeholder="e.g., Product Manager, Doctor, Entrepreneur"
                      className="h-12 rounded-xl border-[#DDD] focus:border-[#1A1A1A]"
                      data-testid="input-profession"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Company/Organization</label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Where do you work?"
                      className="h-12 rounded-xl border-[#DDD] focus:border-[#1A1A1A]"
                      data-testid="input-company"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">City *</label>
                    <Input
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="e.g., Mumbai, Bangalore, Delhi"
                      className="h-12 rounded-xl border-[#DDD] focus:border-[#1A1A1A]"
                      data-testid="input-location"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Areas of Expertise (comma-separated)</label>
                    <Input
                      name="expertise"
                      value={formData.expertise}
                      onChange={handleInputChange}
                      placeholder="e.g., AI, Finance, Healthcare, Marketing"
                      className="h-12 rounded-xl border-[#DDD] focus:border-[#1A1A1A]"
                      data-testid="input-expertise"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Short Bio</label>
                    <Textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleInputChange}
                      placeholder="Tell us a bit about yourself..."
                      className="min-h-[100px] rounded-xl border-[#DDD] focus:border-[#1A1A1A] resize-none"
                      data-testid="input-bio"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Interests */}
            {currentStep === 3 && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <h1 className="font-serif text-3xl text-[#1A1A1A] mb-2">What makes you, you</h1>
                  <p className="text-[#666]">Your interests help us find your people</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Hobbies & Interests * (comma-separated)</label>
                    <Textarea
                      name="hobbies"
                      value={formData.hobbies}
                      onChange={handleInputChange}
                      placeholder="e.g., Hiking, Reading, Photography, Coffee, Board Games, Live Music"
                      className="min-h-[100px] rounded-xl border-[#DDD] focus:border-[#1A1A1A] resize-none"
                      data-testid="input-hobbies"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Travel Bucket List (comma-separated)</label>
                    <Textarea
                      name="travel_bucket_list"
                      value={formData.travel_bucket_list}
                      onChange={handleInputChange}
                      placeholder="e.g., Japan, Iceland, Ladakh, New Zealand, Portugal"
                      className="min-h-[100px] rounded-xl border-[#DDD] focus:border-[#1A1A1A] resize-none"
                      data-testid="input-travel"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">What are you looking for? *</label>
                    <select
                      name="looking_for"
                      value={formData.looking_for}
                      onChange={handleInputChange}
                      className="w-full h-12 rounded-xl border border-[#DDD] focus:border-[#1A1A1A] px-4 bg-white"
                      data-testid="input-looking-for"
                    >
                      <option value="">Select what interests you</option>
                      <option value="travel-companions">Travel Companions</option>
                      <option value="local-friends">Local Friends for Meetups</option>
                      <option value="both">Both Travel & Local Connections</option>
                      <option value="professional-network">Professional Network</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Statement of Intent */}
            {currentStep === 4 && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <h1 className="font-serif text-3xl text-[#1A1A1A] mb-2">Why Havns?</h1>
                  <p className="text-[#666]">Help us understand your intent</p>
                </div>

                <div className="bg-[#FFF8E7] border border-[#C5B358]/30 rounded-2xl p-6 mb-6">
                  <h3 className="font-medium text-[#1A1A1A] mb-2">Remember: Havns is strictly platonic</h3>
                  <p className="text-[#666] text-sm">
                    We're a community for single-by-choice professionals seeking friendships and travel companions. 
                    Romantic advances are not tolerated and result in immediate removal.
                  </p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Why do you want to join Havns? *
                    </label>
                    <Textarea
                      name="statement_of_intent"
                      value={formData.statement_of_intent}
                      onChange={handleInputChange}
                      placeholder="Tell us what being single-by-choice means to you, and what kind of connections you're hoping to make..."
                      className="min-h-[150px] rounded-xl border-[#DDD] focus:border-[#1A1A1A] resize-none"
                      data-testid="input-intent"
                    />
                  </div>
                </div>

                <div className="bg-white border border-[#EEE] rounded-2xl p-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" className="mt-1" required />
                    <span className="text-sm text-[#666]">
                      I understand that Havns is a <strong>strictly platonic</strong> community. 
                      I agree to the <a href="/manifesto" className="text-[#1A1A1A] underline">Anti-Dating Manifesto</a>, 
                      <a href="/privacy" className="text-[#1A1A1A] underline"> Privacy Policy</a>, and 
                      <a href="/terms" className="text-[#1A1A1A] underline"> Terms of Service</a>.
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-12">
              {currentStep > 1 ? (
                <Button
                  onClick={prevStep}
                  variant="ghost"
                  className="text-[#666] hover:text-[#1A1A1A]"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              ) : (
                <Button
                  onClick={() => navigate("/")}
                  variant="ghost"
                  className="text-[#666] hover:text-[#1A1A1A]"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Home
                </Button>
              )}

              {currentStep < 4 ? (
                <Button
                  onClick={nextStep}
                  className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-8 py-6"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-8 py-6"
                >
                  {loading ? "Submitting..." : "Submit Application"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </>
        )}

        {/* Success Step */}
        {currentStep === 5 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-8">
              <Check className="w-10 h-10 text-[#4CAF50]" />
            </div>
            <h1 className="font-serif text-4xl text-[#1A1A1A] mb-4">Application Submitted!</h1>
            <p className="text-[#666] text-lg mb-8 max-w-md mx-auto">
              Thank you for your interest in Havns. We'll review your application and 
              get back to you within 24-48 hours.
            </p>
            <div className="bg-white border border-[#EEE] rounded-2xl p-6 max-w-md mx-auto mb-8">
              <h3 className="font-medium text-[#1A1A1A] mb-2">What happens next?</h3>
              <ul className="text-[#666] text-sm text-left space-y-2">
                <li>✓ Our team reviews your application</li>
                <li>✓ You'll receive an email with the decision</li>
                <li>✓ If approved, complete payment (₹999/month)</li>
                <li>✓ Complete Aadhaar KYC verification</li>
                <li>✓ Start connecting with fellow Havns members!</li>
              </ul>
            </div>
            <Button
              onClick={() => navigate("/")}
              className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-8 py-6"
            >
              Back to Home
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default Onboarding;
